import { createClient, SupabaseClient } from "@supabase/supabase-js";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
    console.warn(
        "[MediFlow Pflege] Supabase nicht konfiguriert. Bitte VITE_SUPABASE_URL und VITE_SUPABASE_ANON_KEY in .env.local setzen.",
    );
}

const safeStorage =
    typeof window !== "undefined" && window.localStorage ? window.localStorage : undefined;

/**
 * In-memory lock — replaces Navigator LockManager to avoid 10s timeout deadlocks.
 * See: https://github.com/supabase/auth-js/issues/888
 */
const inMemoryLock: {
    acquiredLocks: Set<string>;
    queue: Array<{ name: string; resolve: () => void }>;
} = { acquiredLocks: new Set(), queue: [] };

function navigatorLockNoOp<R>(
    name: string,
    _acquireTimeout: number,
    fn: (lock: unknown) => Promise<R>,
): Promise<R> {
    if (!inMemoryLock.acquiredLocks.has(name)) {
        inMemoryLock.acquiredLocks.add(name);
        return fn(name).finally(() => {
            inMemoryLock.acquiredLocks.delete(name);
            const next = inMemoryLock.queue.findIndex((q) => q.name === name);
            if (next >= 0) {
                const item = inMemoryLock.queue.splice(next, 1)[0];
                item?.resolve();
            }
        });
    }

    return new Promise<void>((resolve) => {
        inMemoryLock.queue.push({ name, resolve });
    }).then(() => {
        inMemoryLock.acquiredLocks.add(name);
        return fn(name).finally(() => {
            inMemoryLock.acquiredLocks.delete(name);
            const next = inMemoryLock.queue.findIndex((q) => q.name === name);
            if (next >= 0) {
                const item = inMemoryLock.queue.splice(next, 1)[0];
                item?.resolve();
            }
        });
    });
}

export const supabase: SupabaseClient = createClient(
    supabaseUrl || "https://placeholder.supabase.co",
    supabaseAnonKey || "placeholder-key",
    {
        auth: {
            storage: safeStorage as never,
            autoRefreshToken: true,
            persistSession: Boolean(safeStorage),
            detectSessionInUrl: typeof window !== "undefined",
            lock: navigatorLockNoOp as never,
        },
        realtime: {
            params: { eventsPerSecond: 10 },
        },
    },
);

export function isSupabaseConfigured(): boolean {
    return Boolean(
        supabaseUrl && supabaseAnonKey && supabaseUrl !== "https://placeholder.supabase.co",
    );
}

export function requireSupabase(): SupabaseClient {
    if (!isSupabaseConfigured()) {
        throw new Error("Supabase ist nicht konfiguriert. Bitte .env.local Datei prüfen.");
    }
    return supabase;
}

export async function syncRealtimeAuth(accessToken?: string | null): Promise<void> {
    if (!isSupabaseConfigured() || !accessToken) return;

    try {
        await supabase.realtime.setAuth(accessToken);
    } catch (error) {
        console.warn("[MediFlow Pflege] Realtime-Auth konnte nicht gesetzt werden:", error);
    }
}

export async function ensureValidSession(): Promise<string | null> {
    if (!isSupabaseConfigured()) return null;

    const {
        data: { session },
        error,
    } = await supabase.auth.getSession();

    if (error) {
        console.warn("[MediFlow Pflege] Session-Check fehlgeschlagen:", error.message);
        return null;
    }

    if (!session) {
        console.warn("[MediFlow Pflege] Keine aktive Session gefunden");
        return null;
    }

    const expiresAt = session.expires_at ?? 0;
    const now = Math.floor(Date.now() / 1000);

    if (expiresAt - now < 30) {
        const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
        if (refreshError || !refreshData.session) {
            console.error("[MediFlow Pflege] Session-Refresh fehlgeschlagen:", refreshError?.message);
            return null;
        }
        await syncRealtimeAuth(refreshData.session.access_token);
        return refreshData.session.access_token;
    }

    await syncRealtimeAuth(session.access_token);
    return session.access_token;
}
